import { ChevronDown } from "lucide-react"
import { type ReactNode, useId, useState } from "react"

interface FaqAccordionProps {
  items: { question: string; answer: ReactNode }[]
  /** Index of the item that starts open. */
  defaultOpen?: number
}

export const FaqAccordion = ({ items, defaultOpen }: FaqAccordionProps) => {
  const [open, setOpen] = useState<ReadonlySet<number>>(
    new Set(defaultOpen === undefined ? [] : [defaultOpen]),
  )
  const baseId = useId()
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (!next.delete(i)) next.add(i)
      return next
    })
  return (
    <div className="grid gap-2">
      {items.map(({ question, answer }, i) => {
        const isOpen = open.has(i)
        const panelId = `${baseId}-${i}`
        return (
          <div
            key={question}
            className={`bg-card rounded-lg border px-4 ${isOpen ? "border-primary" : "border-border"}`}
          >
            <h3>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(i)}
                className="focus-visible:ring-ring flex min-h-13 w-full cursor-pointer items-center justify-between gap-3 rounded-sm text-left text-base font-semibold outline-none focus-visible:ring-2"
              >
                {question}
                <ChevronDown
                  aria-hidden="true"
                  className={`text-muted-foreground size-5 shrink-0 transition-transform motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </h3>
            <div
              id={panelId}
              hidden={!isOpen}
              className="text-muted-foreground max-w-[70ch] pb-4 leading-relaxed"
            >
              {answer}
            </div>
          </div>
        )
      })}
    </div>
  )
}
